import { z } from 'zod';
/**
 * Mã lỗi có cấu trúc dùng chung FE↔BE.
 *
 * Frontend `switch` trên `code` — KHÔNG BAO GIỜ parse `message`.
 * Thêm mã mới phải cập nhật bảng ở docs/api-conventions.md §5 trong cùng PR.
 */
export const ErrorCode = {
    VALIDATION_FAILED: 'VALIDATION_FAILED',
    UNAUTHENTICATED: 'UNAUTHENTICATED',
    TOKEN_EXPIRED: 'TOKEN_EXPIRED',
    FORBIDDEN: 'FORBIDDEN',
    /**
     * Nhìn thấy việc nhưng không sửa được — chỉ sửa việc giao cho mình hoặc do
     * mình tạo (APM-TASK-own). Mã riêng để câu báo nói **lý do** thay vì câu
     * chung "không có quyền" — người dùng chốt 2026-09-25.
     */
    TASK_NOT_EDITABLE: 'TASK_NOT_EDITABLE',
    /**
     * Không được sửa giờ thực tế của việc này — APM-ACTH-b. Chỉ PM, quản trị và
     * quản lý của người được giao (theo cây báo cáo) sửa được; người làm thì không.
     */
    ACTUAL_HOURS_NOT_EDITABLE: 'ACTUAL_HOURS_NOT_EDITABLE',
    /**
     * Rule đổi trạng thái của dự án không cho người này kéo việc này — APM-TSP-b.
     * Hai mã riêng để câu báo nói **rule nào** chặn: kéo giữa các cột, hay chạm
     * vào cột hoàn thành.
     */
    TASK_STATUS_FORBIDDEN: 'TASK_STATUS_FORBIDDEN',
    TASK_DONE_FORBIDDEN: 'TASK_DONE_FORBIDDEN',
    /**
     * Chưa đủ thời gian tối thiểu từ lúc bắt đầu tới lúc chuyển sang Xong — APM-TMIN-a.
     * `details`: `minMinutes`, `startedAt`, `earliestAt`, `remainingMinutes`,
     * `startSource` (`actual` | `created`) — để giao diện mở hộp thoại khai lại giờ bắt đầu.
     */
    TASK_COMPLETED_TOO_SOON: 'TASK_COMPLETED_TOO_SOON',
    /**
     * Việc còn thiếu hạn hoặc ước lượng giờ nên chưa được sang "đang làm" / "xong" —
     * APM-EREF-a. `details.missing`: `('dueDate' | 'estimateHours')[]`, để giao diện
     * mở đúng ô cần điền.
     */
    TASK_PLAN_MISSING: 'TASK_PLAN_MISSING',
    /**
     * Đăng nhập đúng nhưng đang bị khoá ở **mọi** tổ chức mình tham gia —
     * APM-MSUS-b. `details.orgNames` để câu báo nói bị khoá ở đâu. Chỉ trả sau
     * khi đã xác thực xong, nên không dùng được để dò email.
     */
    MEMBERSHIP_SUSPENDED: 'MEMBERSHIP_SUSPENDED',
    NOT_FOUND: 'NOT_FOUND',
    CONFLICT: 'CONFLICT',
    /**
     * Email đã có tài khoản — APM-AUTH-h.
     *
     * Tách khỏi `CONFLICT` vì `CONFLICT` được dịch thành câu chung "dữ liệu đã
     * thay đổi ở nơi khác", và đó là câu vô nghĩa với lỗi **hay gặp nhất** ở
     * trang đăng ký. Quy ước là tra theo `code` chứ không theo `message`
     * (docs/api-conventions.md §3), nên một câu riêng cần một mã riêng.
     */
    EMAIL_ALREADY_REGISTERED: 'EMAIL_ALREADY_REGISTERED',
    PRECONDITION_FAILED: 'PRECONDITION_FAILED',
    PAYLOAD_TOO_LARGE: 'PAYLOAD_TOO_LARGE',
    RATE_LIMITED: 'RATE_LIMITED',
    STORAGE_NOT_CONFIGURED: 'STORAGE_NOT_CONFIGURED',
    STORAGE_UNAVAILABLE: 'STORAGE_UNAVAILABLE',
    /**
     * Tổ chức đã dùng hết hạn mức dung lượng — APM-QUOTA-a. `details` mang
     * `usedBytes`/`quotaBytes`/`requestedBytes` để câu báo nói được con số.
     */
    STORAGE_QUOTA_EXCEEDED: 'STORAGE_QUOTA_EXCEEDED',
    AI_NOT_CONFIGURED: 'AI_NOT_CONFIGURED',
    AI_BUDGET_EXCEEDED: 'AI_BUDGET_EXCEEDED',
    AI_PROVIDER_ERROR: 'AI_PROVIDER_ERROR',
    AI_REFUSED: 'AI_REFUSED',
    /**
     * Model trả kết quả không qua được Verify (sai schema, hoặc `finalize` loại)
     * sau hết số lần sửa — APM-H-b. Là lỗi của **ta hoặc của model**, không phải
     * của nhà cung cấp: tách khỏi `AI_PROVIDER_ERROR` để đếm được "hỏng vì gì".
     */
    AI_OUTPUT_INVALID: 'AI_OUTPUT_INVALID',
    /** Run chạm một trần của `AiRunLimits` (lượt, token, tiền, tool call) — APM-H-c. */
    AI_RUN_LIMIT: 'AI_RUN_LIMIT',
    /** Admin đã tắt skill này cho tổ chức (`ai.policy.disabled_skills`) — APM-H-c. */
    AI_SKILL_DISABLED: 'AI_SKILL_DISABLED',
    /** Bản prompt ghi đè bị quét tĩnh chặn (thiếu `{{dynamic}}`, giả thẻ dữ liệu) — APM-H-f. */
    AI_PROMPT_REJECTED: 'AI_PROMPT_REJECTED',
    /** Người bấm gọi AI quá tần suất cho phép (`ai.policy.rate_*`) — APM-H-o. 429 kèm `retryAfterSec`. */
    AI_RATE_LIMITED: 'AI_RATE_LIMITED',
    /** Sáu mã có địa chỉ — APM-H-m. Stage, thông điệp, gợi ý vận hành ở `AI_ERROR_CATALOG`. */
    AI_CONTEXT_OVERFLOW: 'AI_CONTEXT_OVERFLOW',
    AI_TOOL_TERMINAL: 'AI_TOOL_TERMINAL',
    AI_RUN_ABANDONED: 'AI_RUN_ABANDONED',
    AI_RUN_NOT_RESUMABLE: 'AI_RUN_NOT_RESUMABLE',
    AI_RESUME_MODEL_CHANGED: 'AI_RESUME_MODEL_CHANGED',
    AI_PROVIDER_TIMEOUT: 'AI_PROVIDER_TIMEOUT',
    AI_PROVIDER_RATE_LIMITED: 'AI_PROVIDER_RATE_LIMITED',
    AI_MODEL_UNAVAILABLE: 'AI_MODEL_UNAVAILABLE',
    /** Golden set đỏ khi kích hoạt prompt — APM-H-n. */
    AI_GOLDEN_FAILED: 'AI_GOLDEN_FAILED',
    /**
     * Thước đo lọt vào thứ model đọc — APM-H-an: prompt ghi đè, mục kiến thức hay
     * ghi chú bộ nhớ chứa tên ca golden, nguyên văn một khẳng định golden, hoặc
     * một từ trong `MEASUREMENT_LEAK_TERMS`. `details.leaks[]` nói chuỗi nào ở trường nào.
     */
    AI_MEASUREMENT_LEAK: 'AI_MEASUREMENT_LEAK',
    /**
     * Kích hoạt prompt thiếu bản ghi bằng chứng — APM-H-ao: không có `experience`
     * (trên body lẫn trên nháp), bản lưu không đúng schema, hoặc `evidence.runIds`
     * trỏ run không tồn tại / không phải `sandbox` / khác skill / khác tổ chức.
     */
    AI_PROMPT_NO_EVIDENCE: 'AI_PROMPT_NO_EVIDENCE',
    /** Tổ chức chưa có token Figma đang hiệu lực — docs/plan-design-control.md (APM-DC-c). */
    FIGMA_NOT_CONFIGURED: 'FIGMA_NOT_CONFIGURED',
    /** Figma từ chối token (sai, đã thu hồi) hoặc token không xem được file này. */
    FIGMA_FORBIDDEN: 'FIGMA_FORBIDDEN',
    /** Figma trả 404: link sai, file đã xoá, hoặc nút `node-id` không còn. */
    FIGMA_FILE_NOT_FOUND: 'FIGMA_FILE_NOT_FOUND',
    /**
     * Hết lượt gọi Figma — của Figma (`details.source = 'figma'`, kèm `planTier`,
     * `rateLimitType`) hoặc của bộ giới hạn phía ta (`'local'`). `details.retryAfterSec`.
     */
    FIGMA_RATE_LIMITED: 'FIGMA_RATE_LIMITED',
    /** Không gọi được Figma: mạng, quá hạn, hoặc Figma trả 5xx. */
    FIGMA_UNAVAILABLE: 'FIGMA_UNAVAILABLE',
    /** Một phản hồi Figma vượt trần đọc vào bộ nhớ (§7.1 Ngoại lệ 3). `details.maxBytes`. */
    DESIGN_PAGE_TOO_LARGE: 'DESIGN_PAGE_TOO_LARGE',
    /** Bản cài đặt chưa có bot Telegram đang hiệu lực — docs/plan-telegram.md. */
    TELEGRAM_NOT_CONFIGURED: 'TELEGRAM_NOT_CONFIGURED',
    /** Telegram từ chối lời gọi: token sai, bot bị xoá, hoặc tham số không hợp lệ. */
    TELEGRAM_API_ERROR: 'TELEGRAM_API_ERROR',
    /**
     * Hết lượt gọi Telegram — của Telegram (429 kèm `retry_after`) hoặc của bộ
     * giới hạn phía ta. `details.retryAfterSec`.
     */
    TELEGRAM_RATE_LIMITED: 'TELEGRAM_RATE_LIMITED',
    /**
     * Tài khoản Telegram đang gọi chưa liên kết với tài khoản nào trong hệ thống.
     *
     * Không phải lỗi hệ thống: đây là màn hình Mini App mời người dùng vào web bấm
     * Kết nối. Trả 403 chứ không 401 — 401 sẽ bị `apiFetch` hiểu là phiên hết hạn
     * và kéo theo một lượt refresh vô nghĩa.
     */
    TELEGRAM_NOT_LINKED: 'TELEGRAM_NOT_LINKED',
    /**
     * Sửa, chốt lại hoặc bỏ một báo cáo công việc team **đã chốt** — APM-TWR-c.
     *
     * Tách khỏi `CONFLICT` vì câu chung của mã đó ("dữ liệu đã thay đổi ở nơi
     * khác") gợi ý tải lại rồi thử lại, mà thử lại bao nhiêu lần cũng vậy: bản đã
     * chốt là thứ leader đã gửi đi, nó đứng yên. Muốn khác thì tạo bản mới.
     */
    TEAM_REPORT_FINALIZED: 'TEAM_REPORT_FINALIZED',
    /**
     * Việc chưa đủ chi tiết để AI ước lượng/giao việc — APM-AIA-a: cần checklist
     * hoặc mô tả trên 50 từ (`isDetailedEnough`). Tách khỏi `VALIDATION_FAILED`
     * vì câu báo phải chỉ **cách sửa** ("mô tả kỹ hơn"), không phải "dữ liệu
     * chưa hợp lệ" — dữ liệu hoàn toàn hợp lệ, chỉ là chưa đủ để đoán.
     */
    TASK_NOT_DETAILED_ENOUGH: 'TASK_NOT_DETAILED_ENOUGH',
    EVENT_SEQUENCE_CONFLICT: 'EVENT_SEQUENCE_CONFLICT',
    INTERNAL: 'INTERNAL',
    /**
     * Link chia sẻ tài liệu không mở được — APM-SHR-d: không có, đã thu hồi, hết hạn, hoặc tài liệu
     * đã xoá. **Một** mã cho cả bốn: phân biệt là nói cho người cầm link biết token từng có thật.
     */
    SHARE_UNAVAILABLE: 'SHARE_UNAVAILABLE',
    /**
     * Bản dịch một khối vẫn bị cắt sau khi đã nhân đôi trần output (APM-BIL-a, plan-chilldoc-bilingual
     * §3.2) — khối quá dài để dịch trong một lượt: chia đoạn rồi dịch lại.
     */
    TRANSLATION_TOO_LONG: 'TRANSLATION_TOO_LONG',
    /**
     * Khối đã ở đúng ngôn ngữ đích (APM-BIL-page) — máy chủ không gửi nó cho model. Lỗi **của một
     * khối** trong kết quả lượt dịch, không phải lỗi của cả request.
     */
    TRANSLATION_SAME_LANGUAGE: 'TRANSLATION_SAME_LANGUAGE',
    /**
     * Model trả thiếu hay hỏng **một khối** của lượt dịch theo trang (mất biến / đường dẫn / mã, trả
     * lời thay vì dịch) — các khối khác vẫn lưu; dịch lại riêng khối này (APM-BIL-page).
     */
    TRANSLATION_BLOCK_FAILED: 'TRANSLATION_BLOCK_FAILED',
    /** Link chỉ dành cho thành viên tổ chức của chủ tệp, người gọi không thuộc tổ chức ấy — APM-SHR-d. */
    SHARE_ORG_ONLY: 'SHARE_ORG_ONLY',
    /** Mời theo email: không có tài khoản ChillPM nào mang email ấy — APM-SHR-c. */
    SHARE_EMAIL_NOT_FOUND: 'SHARE_EMAIL_NOT_FOUND',
    /**
     * Gộp / tách tổ chức — docs/plan-org-restructure.md §4 L13.
     *
     * - `IN_PROGRESS` (409): tổ chức đã là bên của một đề xuất đang mở.
     * - `DEMO` (403): tổ chức demo không là bên của lượt gộp / tách nào (L7).
     * - `NOT_APPROVER` (403): người gọi không nằm trong tập người duyệt của đề xuất.
     * - `STALE_VERSION` (409): cấu hình đã đổi từ lúc người gọi mở — `details.configVersion`.
     * - `BLOCKED` (409): preflight có lỗi chặn (APM-ORGMS-d).
     * - `ORG_RESTRUCTURING` (423): tổ chức đang đóng băng vì lượt chạy (APM-ORGMS-e).
     */
    ORG_RESTRUCTURE_IN_PROGRESS: 'ORG_RESTRUCTURE_IN_PROGRESS',
    ORG_RESTRUCTURE_DEMO: 'ORG_RESTRUCTURE_DEMO',
    ORG_RESTRUCTURE_NOT_APPROVER: 'ORG_RESTRUCTURE_NOT_APPROVER',
    ORG_RESTRUCTURE_STALE_VERSION: 'ORG_RESTRUCTURE_STALE_VERSION',
    ORG_RESTRUCTURE_BLOCKED: 'ORG_RESTRUCTURE_BLOCKED',
    ORG_RESTRUCTURING: 'ORG_RESTRUCTURING',
    /**
     * Không gian cá nhân (tổ chức cá nhân) — docs/plan-personal-space.md §3.3.
     *
     * - `RESTRICTED` (403): tính năng không có ở không gian cá nhân — `details.feature`
     *   (`PersonalSpaceFeature`: mời người, tài liệu, đính kèm, Nhân sự, Thay đổi, Thiết kế).
     * - `TASK_LIMIT` (403): đã chạm `PERSONAL_SPACE_MAX_TASKS` — `details.limit`, `details.current`.
     *
     * Tách khỏi `FORBIDDEN` vì câu báo phải chỉ **lối ra** ("tham gia hoặc lập tổ chức"), không phải
     * "không có quyền": người dùng là chủ, quyền thì đủ — chỉ là chỗ này không có tính năng ấy.
     */
    PERSONAL_SPACE_RESTRICTED: 'PERSONAL_SPACE_RESTRICTED',
    PERSONAL_SPACE_TASK_LIMIT: 'PERSONAL_SPACE_TASK_LIMIT',
    /**
     * Sổ chốt phương án — APM-DSHEET, docs/plan-decision-sheet.md §4.
     *
     * - `SHEET_NOT_READY` (409): mở sổ cần ≥ 1 câu và ≥ 1 người chốt.
     * - `SHEET_CLOSED` (409): sổ đã đóng (hoặc chưa mở, với lệnh chốt) — mở lại để sửa.
     * - `SHEET_NOT_CLOSED` (409): xuất thành tài liệu dự án khi sổ chưa đóng — người
     *   trả lời vẫn đổi được đáp án, tài liệu chốt sẽ sai ngay sau khi xuất (APM-DSX).
     *   `details.status` (`draft` | `open`).
     * - `NOT_DECIDER` (403): người gọi không nằm trong danh sách người chốt của sổ.
     * - `ANSWER_CONFLICT` (409): `baseRev` lệch — `details` là `DecisionAnswerConflict`.
     * - `OPTION_IN_USE` (409): xoá / đổi mã một phương án đang là đáp án.
     * - `QUESTION_ANSWERED` (409): xoá một câu đã chốt — bỏ chốt trước.
     * - `DECIDER_NO_ACCESS` (400): người được thêm không phải thành viên dự án, hoặc vai
     *   của họ không có `document:read` — `details.userIds`.
     * - `IMPORT_INVALID` (400): tệp JSON `apm-decision-sheet` sai — `details.issues`
     *   (`DecisionImportIssue[]`, đủ mọi lỗi kèm đường dẫn).
     * - `SHEET_NOT_YET` (404): đọc với `asOf` trước lúc tạo sổ (APM-DSHEET-b).
     * - `SUGGESTION_REVIEWED` (409): gợi ý AI của sổ đã được duyệt / bỏ rồi —
     *   `details.status` (APM-DSHEET-g/h).
     * - `SUGGESTION_UNUSABLE` (409): gợi ý không phải của hai skill sổ, hoặc nội
     *   dung không còn đọc được thành JSON v1 — `details.skill`.
     *
     * Link công khai — APM-DSX-a, plan §11.3:
     * - `SHARE_UNAVAILABLE` (404): **mọi** lỗi token — không có, thu hồi, hết hạn,
     *   sổ đã xoá. Một mã để người cầm link không dò được token nào từng có thật.
     * - `SHARE_READ_ONLY` (403): link `view` mà gọi chốt / bỏ chốt.
     * - `SHARE_RATE_LIMITED` (429 + `Retry-After`): quá 30 lần ghi / 10 phút /
     *   (IP, link) — `details.retryAfterSec`.
     * - `RESPONDENT_REQUIRED` (400): chốt qua link khi chưa đăng nhập mà thiếu
     *   `respondent` (tên + email).
     */
    DECISION_SHEET_NOT_READY: 'DECISION_SHEET_NOT_READY',
    DECISION_SHEET_CLOSED: 'DECISION_SHEET_CLOSED',
    DECISION_SHEET_NOT_CLOSED: 'DECISION_SHEET_NOT_CLOSED',
    DECISION_NOT_DECIDER: 'DECISION_NOT_DECIDER',
    DECISION_ANSWER_CONFLICT: 'DECISION_ANSWER_CONFLICT',
    DECISION_OPTION_IN_USE: 'DECISION_OPTION_IN_USE',
    DECISION_QUESTION_ANSWERED: 'DECISION_QUESTION_ANSWERED',
    DECISION_DECIDER_NO_ACCESS: 'DECISION_DECIDER_NO_ACCESS',
    DECISION_IMPORT_INVALID: 'DECISION_IMPORT_INVALID',
    DECISION_SHEET_NOT_YET: 'DECISION_SHEET_NOT_YET',
    DECISION_SUGGESTION_REVIEWED: 'DECISION_SUGGESTION_REVIEWED',
    DECISION_SUGGESTION_UNUSABLE: 'DECISION_SUGGESTION_UNUSABLE',
    DECISION_SHARE_UNAVAILABLE: 'DECISION_SHARE_UNAVAILABLE',
    DECISION_SHARE_READ_ONLY: 'DECISION_SHARE_READ_ONLY',
    DECISION_SHARE_RATE_LIMITED: 'DECISION_SHARE_RATE_LIMITED',
    DECISION_RESPONDENT_REQUIRED: 'DECISION_RESPONDENT_REQUIRED',
    /**
     * Đồng bộ dự án với nguồn ngoài — APM-SYNC-a, docs/plan-project-sync.md §6.1.
     *
     * Lỗi từ phía bên kia (`AiserviceClient`) tách theo **việc người dùng phải
     * làm**: sai địa chỉ (`UNREACHABLE`), key chết (`KEY_INVALID`), bên kia từ
     * chối dữ liệu (`REMOTE_REJECTED`, `details.errors` là thông điệp từng
     * trường), không có (`REMOTE_NOT_FOUND`), bên kia hỏng (`REMOTE_ERROR`), quá
     * nhịp (`RATE_LIMITED`, `details.retryAfterSec` + `details.source`).
     */
    SYNC_PROVIDER_NOT_FOUND: 'SYNC_PROVIDER_NOT_FOUND',
    SYNC_PROVIDER_DISABLED: 'SYNC_PROVIDER_DISABLED',
    SYNC_PROVIDER_IN_USE: 'SYNC_PROVIDER_IN_USE',
    SYNC_PROVIDER_UNREACHABLE: 'SYNC_PROVIDER_UNREACHABLE',
    SYNC_KEY_INVALID: 'SYNC_KEY_INVALID',
    SYNC_EXTERNAL_PROJECT_TAKEN: 'SYNC_EXTERNAL_PROJECT_TAKEN',
    SYNC_RATE_LIMITED: 'SYNC_RATE_LIMITED',
    SYNC_REMOTE_ERROR: 'SYNC_REMOTE_ERROR',
    SYNC_REMOTE_REJECTED: 'SYNC_REMOTE_REJECTED',
    SYNC_REMOTE_NOT_FOUND: 'SYNC_REMOTE_NOT_FOUND',
    SYNC_RESPONSE_TOO_LARGE: 'SYNC_RESPONSE_TOO_LARGE',
    SYNC_PRIMARY_EXISTS: 'SYNC_PRIMARY_EXISTS',
    SYNC_CONNECTION_NOT_FOUND: 'SYNC_CONNECTION_NOT_FOUND',
    SYNC_RUNNING: 'SYNC_RUNNING',
    /** APM-SYNC-c: key mới trỏ một dự án ngoài **khác** dự án của kết nối. */
    SYNC_EXTERNAL_PROJECT_MISMATCH: 'SYNC_EXTERNAL_PROJECT_MISMATCH',
    /** APM-SYNC-c: thao tác trên kết nối đã ngắt (chỉ còn xem nhật ký). */
    SYNC_CONNECTION_DISCONNECTED: 'SYNC_CONNECTION_DISCONNECTED',
    /**
     * APM-SYNC-alert: dự án chưa có loại việc nào ⇒ không tạo được việc nào từ
     * nguồn. Lỗi của **cả lượt**, không phải của từng việc: bỏ qua từng việc thì
     * lượt vẫn "xong", con trỏ nhảy qua, và các việc ấy không bao giờ được kéo lại.
     */
    SYNC_NO_TASK_TYPE: 'SYNC_NO_TASK_TYPE',
    /**
     * APM-SYNC2-a: loại nguồn có trong danh sách (`SyncProviderKind`) nhưng bản cài
     * này chưa có adapter — tạo nguồn / kết nối loại ấy bị từ chối (400).
     */
    SYNC_PROVIDER_KIND_UNSUPPORTED: 'SYNC_PROVIDER_KIND_UNSUPPORTED',
    /**
     * Tab Git — APM-GIT-b, docs/plan-git-integration.md §3, §5.1.
     *
     * Tách theo **việc người dùng phải làm**, như nhóm `SYNC_*`: nhờ quản trị khai
     * máy chủ (`HOST_UNKNOWN`), sửa địa chỉ / CA (`HOST_UNREACHABLE`,
     * `details.reason`), dán token khác (`TOKEN_INVALID`), xin thêm quyền cho token
     * (`FORBIDDEN`), kiểm lại đường dẫn kho (`REPO_NOT_FOUND`), chờ (`RATE_LIMITED`,
     * `details.retryAfterSec`).
     */
    GIT_HOST_NOT_FOUND: 'GIT_HOST_NOT_FOUND',
    /** Link kho không khớp `web_base_url` của máy chủ nào đã khai. */
    GIT_HOST_UNKNOWN: 'GIT_HOST_UNKNOWN',
    /** Xoá máy chủ còn kết nối dự án (kể cả đã thu hồi) hoặc tài khoản đã gắn tay. */
    GIT_HOST_IN_USE: 'GIT_HOST_IN_USE',
    GIT_HOST_DISABLED: 'GIT_HOST_DISABLED',
    GIT_HOST_UNREACHABLE: 'GIT_HOST_UNREACHABLE',
    GIT_TOKEN_INVALID: 'GIT_TOKEN_INVALID',
    /** Token hợp lệ nhưng thiếu quyền cho thao tác này (scope / vai trong kho). */
    GIT_FORBIDDEN: 'GIT_FORBIDDEN',
    GIT_REPO_NOT_FOUND: 'GIT_REPO_NOT_FOUND',
    GIT_BRANCH_EXISTS: 'GIT_BRANCH_EXISTS',
    GIT_RATE_LIMITED: 'GIT_RATE_LIMITED',
    /**
     * APM-GIT-c: loại máy chủ chưa có adapter ở bản cài này, hoặc adapter của nó
     * chưa cài thao tác được hỏi (`details.kind`, `details.feature`) — 400.
     */
    GIT_NOT_SUPPORTED: 'GIT_NOT_SUPPORTED',
    /**
     * APM-GIT-d — kết nối và kho của dự án (§3.2, §3.3), đều 409:
     * thu hồi kết nối còn kho đang gắn (`details.repositoryCount`) · dán link kho
     * của một máy chủ mà dự án chưa có kết nối dùng được (`details.hostId`) · gắn
     * một kho đã gắn vào dự án rồi (`details.repositoryId`).
     */
    GIT_CONNECTION_IN_USE: 'GIT_CONNECTION_IN_USE',
    GIT_CONNECTION_REQUIRED: 'GIT_CONNECTION_REQUIRED',
    GIT_REPO_ALREADY_LINKED: 'GIT_REPO_ALREADY_LINKED',
    /**
     * APM-GIT-s — gắn cả nhóm kho (plan-git-integration §16): nhóm GitLab /
     * organization GitHub không tồn tại hoặc token không thấy (404) · nhóm đã gắn vào
     * dự án rồi, kể cả qua một kết nối khác tới cùng máy chủ (409, `details.groupId`).
     */
    GIT_GROUP_NOT_FOUND: 'GIT_GROUP_NOT_FOUND',
    GIT_GROUP_ALREADY_LINKED: 'GIT_GROUP_ALREADY_LINKED',
    /**
     * APM-PCOM — gắn cập nhật tiến độ với commit (docs/plan-progress-commits.md):
     * bật công tắc khi dự án chưa có kho dùng được (400, `details.reason`) · báo một
     * commit không có trong 100 commit mới nhất của nhánh (400, `details.sha`).
     */
    GIT_NOT_CONNECTED: 'GIT_NOT_CONNECTED',
    GIT_COMMIT_NOT_FOUND: 'GIT_COMMIT_NOT_FOUND',
    /**
     * APM-ORGINT-d — địa chỉ nguồn đồng bộ / máy chủ git trỏ vào mạng nội bộ
     * (loopback, RFC1918, link-local, CGNAT, ULA…) mà tổ chức chưa được super admin
     * bật "cho phép địa chỉ nội bộ" — 400. Lúc lưu: `details` theo trường
     * (`{ apiBaseUrl: [...] }`); lúc gọi: `details.host`.
     */
    PRIVATE_ADDRESS_BLOCKED: 'PRIVATE_ADDRESS_BLOCKED',
};
export const ApiError = z.object({
    error: z.object({
        code: z.enum(Object.values(ErrorCode)),
        /** Chuỗi hiển thị được cho người dùng, tiếng Việt. */
        message: z.string(),
        details: z.record(z.string(), z.unknown()).optional(),
        traceId: z.string().optional(),
    }),
});
//# sourceMappingURL=errors.js.map